// @flow @format
import React, {Component} from 'react';
import Modal from '../../../../components/Modal';
import FilesLoading from '../Loading';
import FolderIcon from '../../../../components/Icon/folder-o';
import Tooltip from '../../../../components/Tooltip';
import WarningIcon from '../../../../components/Icon/exclamation-triangle.js';
import FileIcon from '../../../../components/Icon/file-text-o';
import LoadingIcon from '../../../../components/Icon/circle-o-notch';
import DownloadIcon from '../../../../components/Icon/download';
import HistoryIcon from '../../../../components/Icon/history';
import AngleDown from '../../../../components/Icon/angle-down';
import AngleUp from '../../../../components/Icon/angle-up';
import {
	FileListBody,
	FileListItemOptions,
	FileListItemOptionBox,
	FileListItemLink,
	FileListItemRow,
	FileListItemCol,
} from '../../../../components/FileList';
import {toSIString} from 'lib/util';
import classNames from 'lib/classnames';

import type {File} from 'lib/cloner-api';

type Props = {
	files: File[],
	fileNodeId: string,
	versions: {[fileId: string]: ?(File[])},
	sortKey: string,
	sortLt: boolean,
	usedBytes: number,
	grantedBytes: number,
	fileUrl: (fileId: string) => string,
	onKeyChange: (key: $Keys<File>) => void,
	downloadFile: (fileId: string) => void,
	fetchFileVersions: (fileId: string) => void,
};

const FileSorting = (
	key: string,
	lt: boolean,
	onChange: (key: $Keys<File>) => void
) => {
	const Item = ({label, itemKey}) =>
		<span
			className="FilesLayout__Sorting__Item"
			onClick={() => onChange(itemKey)}
		>
			{label} {key === itemKey && (lt ? <AngleDown /> : <AngleUp />)}
		</span>;

	return (
		<div className="FilesLayout__Sorting">
			<span className="FilesLayout__Sorting__Item" />

			<Item label="Nombre" itemKey="name" />

			<Item label="Fecha creación" itemKey="created" />

			<Item label="Tamaño" itemKey="size" />

			<span className="FilesLayout__Sorting__Item" />
		</div>
	);
};

const span = 410;
const limit = 10 * 1024 * 1024 * 1024;

const versionsOverlay = <span>Versiones</span>;
const downloadOverlay = <span>Descargar</span>;

class FileExplorer extends Component<
	Props,
	{
		fileSelected: ?string,
		fileFailed: ?File,
	}
> {
	state = {
		fileSelected: null,
		fileFailed: null,
	};

	_downloadFile = (file: File) => {
		if (!file) {
			return;
		}

		const {usedBytes, grantedBytes} = this.props;
		if (usedBytes + file.size + span >= grantedBytes) {
			this.setState({fileFailed: file});
			return;
		}

		this.props.downloadFile(file.id);
	};

	render() {
		const iconClass = file =>
			classNames({
				FileList__Item__Icon: true,
				'FileList__Item__Icon--Folder': file.type === 0,
			});

		const formatDate = dateStr => {
			const date = new Date(dateStr);
			return `${date.toLocaleDateString()} ${date.toLocaleTimeString(
				'es-ES',
				{hour: '2-digit', minute: '2-digit'}
			)}`;
		};

		return (
			<div>
				{FileSorting(
					this.props.sortKey,
					this.props.sortLt,
					this.props.onKeyChange
				)}

				<FileListBody style={{maxHeight: '76vh', overflowY: 'scroll'}}>
					{this.props.files.map(file =>
						<FileListItemRow
							key={file.id}
							className={classNames({
								'FileList__Item__Row--Deleted': file.deleted,
							})}
						>
							<FileListItemCol>
								{file.id === this.props.fileNodeId
									? <LoadingIcon className="rotate-animation" />
									: file.type === 0
										? <FolderIcon
												className={iconClass(file)}
											/>
										: <FileIcon
												className={iconClass(file)}
											/>}
							</FileListItemCol>

							<FileListItemCol className="FileList__Item__Col__Name">
								{file.type === 0 &&
									<FileListItemLink
										className="FileList__Item__Col__Name"
										to={this.props.fileUrl(file.id)}
									>
										{file.name}
									</FileListItemLink>}
								{file.type === 1 &&
									<p className="FileList__Item__Col__Name">
										{file.name}
									</p>}
							</FileListItemCol>

							<FileListItemCol className="FileList__Item__Col__Deleted">
								{file.deleted && 'eliminado'}
							</FileListItemCol>

							<FileListItemCol className="FileList__Item__Col__Date">
								{formatDate(file.created)}
							</FileListItemCol>

							<FileListItemCol className="FileList__Item__Col__Size">
								{toSIString(file.size)}
							</FileListItemCol>

							<FileListItemOptions>
								<Tooltip
									placement="bottom"
									overlay={downloadOverlay}
								>
									<FileListItemOptionBox
										onClick={() => this._downloadFile(file)}
									>
										<DownloadIcon />
									</FileListItemOptionBox>
								</Tooltip>

								<Tooltip
									placement="bottom"
									overlay={versionsOverlay}
								>
									<FileListItemOptionBox
										disabled={
											file.num_versions === 1 ||
											file.type === 0
										}
										onClick={() => {
											this.props.fetchFileVersions(
												file.id
											);
											this.setState({
												fileSelected: file.id,
											});
										}}
									>
										<HistoryIcon />
									</FileListItemOptionBox>
								</Tooltip>
							</FileListItemOptions>
						</FileListItemRow>
					)}
				</FileListBody>

				<Modal
					visible={!!this.state.fileFailed}
					onClose={() => this.setState({fileFailed: null})}
				>
					{this.state.fileFailed &&
						<div className="DownloadConfirmation">
							<h2>
								{this.state.fileFailed.size >= limit &&
									<span>
										No es posible realizar una descarga
										superior
										a {toSIString(limit)}.
									</span>}

								{this.state.fileFailed.size < limit &&
									<span>
										No hay espacio disposible suficiente
										para
										descargar
										el archivo solicitado.
									</span>}
							</h2>

							{this.state.fileFailed.size >= limit &&
								<div style={{maxWidth: 700}}>
									<p>
										Actualmente por limitaciones del
										navegador Web solo es posible descargar
										archivos hasta {toSIString(limit)}.
									</p>
									<p>
										Si necesita realizar una descarga
										superior este limite,
										intente multiples descargas de menor
										tamaño.
										También puede contactar a nuestra área
										de soporte al correo
										{' '}
										<a href="mailto:soporte@cloner.cl">
											soporte@cloner.cl
										</a>
										{' '}
										o al número
										{' '}
										<a href="tel:+56224920162">
											+56224920162
										</a>
										{' '}
										para solicitar una recuperación.
									</p>
								</div>}

							{this.state.fileFailed.size < limit &&
								<div>
									<p>
										Actualmente tiene
										{' '}
										{toSIString(
											this.props.grantedBytes -
												this.props.usedBytes
										)}
										{' '}
										espacio libre para descargar.

										El archivo solicitado necesita disponer
										de
										{' '}
										{this.state.fileFailed &&
											toSIString(
												this.state.fileFailed.size +
													this.props.usedBytes
											)}
										{' '}
										de espacio libre.
									</p>

									<p>
										Para poder realizar su descarga intente
										borrando
										descargar ya terminadas para liberar
										espacio.
									</p>

									<p
										style={{
											backgroundColor:
												'rgb(181, 216, 209)',
											border:
												'1px solid rgb(149, 210, 198)',
											borderRadius: 5,
											color: 'rgb(51, 109, 98)',
											padding: 'var(--baseline)',
										}}
									>
										<WarningIcon />
										{' '}
										Importante: actualmente por limitaciones
										del
										navegador Web solo es posible descargar
										archivos
										hasta
										10GB.
									</p>
								</div>}

							<div className="DownloadConfirmation__Buttons">
								<button
									className="DownloadConfirmation__Button"
									onClick={() =>
										this.setState({fileFailed: null})}
								>
									Aceptar
								</button>
							</div>
						</div>}
				</Modal>

				<Modal
					visible={!!this.state.fileSelected}
					onClose={() => this.setState({fileSelected: null})}
				>
					<div
						style={{
							backgroundColor: 'var(--background-color)',
							padding: 'var(--baseline)',
							fontSize: 'var(--lg-text)',
						}}
					>
						Versiones
					</div>

					<FileListBody
						style={{
							backgroundColor: 'var(--background-color)',
							maxWidth: 'calc(var(--baseline) * 60)',
							minWidth: 'calc(var(--baseline) * 60)',
							minHeight: '20vh',
							maxHeight: '60vh',
							overflowY: 'scroll',
							overflowX: 'hidden',
							marginTop: 0,
						}}
					>

						{!this.props.versions[
							String(this.state.fileSelected)
						] &&
							<div
								style={{padding: 'calc(var(--baseline) * 2) 0'}}
							>
								<FilesLoading title="Cargando versiones" />
							</div>}

						{this.props.versions[String(this.state.fileSelected)] &&
							(this.props.versions[
								String(this.state.fileSelected)
							] || [])
								.map(file =>
									<FileListItemRow key={file.id}>
										<FileListItemCol>
											{file.type === 0
												? <FolderIcon
														className={iconClass(
															file
														)}
													/>
												: <FileIcon
														className={iconClass(
															file
														)}
													/>}
										</FileListItemCol>

										<FileListItemCol>
											<p className="FileList__Item__Col__Name">
												{file.name}
											</p>
										</FileListItemCol>

										<FileListItemCol className="FileList__Item__Col__Date">
											{formatDate(file.created)}
										</FileListItemCol>
										<FileListItemCol className="FileList__Item__Col__Size">
											{toSIString(file.size)}
										</FileListItemCol>

										<FileListItemOptions>
											<FileListItemOptionBox
												onClick={() =>
													this.props.downloadFile(
														file.id
													)}
											>
												<DownloadIcon />
											</FileListItemOptionBox>
										</FileListItemOptions>
									</FileListItemRow>
								)}
					</FileListBody>
				</Modal>
			</div>
		);
	}
}

export default FileExplorer;



// WEBPACK FOOTER //
// ./src/routes/Explorer/components/FileExplorer/index.js